import React, { useMemo } from "react";
import { PostCard } from "../../helperFunctions/HelperFunctions";
import PrimaryButton from "../../../../DesignSystem/Buttons/PrimaryButton";
import CheckboxGroup from "../../../../DesignSystem/SelectionControls/CheckboxGroup";
import RadioGroup from "../../../../DesignSystem/SelectionControls/RadioGroup";
import Field from "../../../../DesignSystem/Inputs/Field";
import "../../../../../css/Adviser/EditAdviserDetailsPage.css";
import ApplyingAs from "../../Constants/ApplyingAs";
import ApplyingToJoin from "../../Constants/ApplyingToJoin";
import BusinessPermissions from "../../Constants/BusinessPermissions";
import { ReactComponent as BackIcon } from "../../../../../assets/icons/Back.svg";
import { ReactComponent as DeleteIcon } from "../../../../../assets/icons/delete.svg";
import { ReactComponent as AddIcon } from "../../../../../assets/icons/add.svg";
import { formatUKPhone } from "../../utils/formatters";
import Banner from "../../../AML/Components/Banners/Banner";

const pad = (n) => String(n).padStart(2, "0");

const toDateObject = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "object" && value?.seconds != null) {
    const d = new Date(value.seconds * 1000);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split("-").map(Number);
      return new Date(y, m - 1, d);
    }

    if (/^\d{4}-\d{2}$/.test(value)) {
      const [y, m] = value.split("-").map(Number);
      return new Date(y, m - 1, 1);
    }

    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  return null;
};

const toLocalDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value))
    return value;

  const d = toDateObject(value);
  if (!d) return "";

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const toLocalMonthInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}$/.test(value)) return value;

  const d = toDateObject(value);
  if (!d) return "";

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
};
export const EditAdviserDetails = ({
  form,
  setForm,
  saving,
  onSave,
  onCancel,
}) => {

  const updateRegulatoryText = (value) => {
    setForm((prev) => ({
      ...prev,
      adviserDoc: {
        ...(prev?.adviserDoc || {}),
        regulatoryText: value || "",
      },
    }));
  };

  const updateWebsite = (value) => {
    setForm((prev) => ({
      ...prev,
      adviserDoc: {
        ...(prev?.adviserDoc || {}),
        website: value,
      },
    }));
  };

  // console.log("EditAdviserDetails form state:", form);
  const updateAdviserDoc = (key, value) => {
    setForm((prev) => ({
      ...prev,
      adviserDoc: {
        ...(prev?.adviserDoc || {}),
        [key]: value,
      },
    }));
  };

  const updateSection = (section, key, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...(prev?.[section] || {}),
        [key]: value,
      },
    }));
  };

  const updateBusinessAddress = (key, value) => {
    setForm((prev) => ({
      ...prev,
      personalDetails: {
        ...(prev?.personalDetails || {}),
        businessAddress: {
          ...(prev?.personalDetails?.businessAddress || {}),
          [key]: value,
        },
      },
    }));
  };

  const tradingStyles = useMemo(() => {
    if (
      Array.isArray(form?.adviserDoc?.tradingStyles) &&
      form.adviserDoc.tradingStyles.length
    ) {
      return form.adviserDoc.tradingStyles;
    }

    if (form?.adviserDoc?.tradingStyle) {
      return [form.adviserDoc.tradingStyle];
    }

    return [""];
  }, [form?.adviserDoc?.tradingStyles, form?.adviserDoc?.tradingStyle]);

  const updateTradingStyleAtIndex = (index, value) => {
    const next = [...tradingStyles];
    next[index] = value;
    updateAdviserDoc("tradingStyles", next);
  };

  const addTradingStyle = () => {
    updateAdviserDoc("tradingStyles", [...tradingStyles, ""]);
  };

  const removeTradingStyle = (index) => {
    updateAdviserDoc(
      "tradingStyles",
      tradingStyles.filter((_, i) => i !== index),
    );
  };

  return (
    <div className="smFeed">
      <div className="smEditActions">
        <div className="smEditActionsLeft">
          {/* <BackIcon
            className="smBackIcon"
            onClick={onCancel}
            title="Go back without saving"
          /> */}
          <h3 className="FP_H3">Edit Details</h3>
        </div>

        <PrimaryButton
          label={saving ? "Saving..." : "Save Details"}
          onClick={onSave}
          disabled={saving}
        />
      </div>
      {/* <div className="FP_Subtitle_Semibold">Personal Details</div> */}
      <Banner
        type="warning"
        message="For any changes to business related details, please contact the head office."
      />
      <PostCard title="Contact">
        <div className="smGrid2">
          <Field
            label="First name"
            value={form.personalDetails?.forename || ""}
            onChange={(v) => updateSection("personalDetails", "forename", v)}
          />

          <Field
            label="Last name"
            value={form.personalDetails?.surname || ""}
            onChange={(v) => updateSection("personalDetails", "surname", v)}
          />

          <Field
            label="Personal Email"
            type="email"
            value={form.personalDetails?.emailAddress || ""}
            onChange={(v) =>
              updateSection("personalDetails", "emailAddress", v)
            }
          />

          <Field
            label="Mobile"
            type="tel"
            value={formatUKPhone(form.personalDetails?.mobileTelephone) || ""}
            onChange={(v) =>
              updateSection("personalDetails", "mobileTelephone", v)
            }
          />

          <Field
            label="Home"
            type="tel"
            value={formatUKPhone(form.personalDetails?.homeTelephone) || ""}
            onChange={(v) =>
              updateSection("personalDetails", "homeTelephone", v)
            }
          />

          <Field
            label="Office"
            type="tel"
            value={formatUKPhone(form.personalDetails?.officeTelephone) || ""}
            onChange={(v) =>
              updateSection("personalDetails", "officeTelephone", v)
            }
          />
        </div>
      </PostCard>
      <PostCard title="Current Address">
        <div className="smGrid2">
          <Field
            label="Current address"
            full
            value={form.personalDetails?.currentAddress || ""}
            onChange={(v) =>
              updateSection("personalDetails", "currentAddress", v)
            }
          />

          <Field
            label="Town"
            value={form.personalDetails?.currentTown || ""}
            onChange={(v) => updateSection("personalDetails", "currentTown", v)}
          />

          <Field
            label="County"
            value={form.personalDetails?.currentCounty || ""}
            onChange={(v) =>
              updateSection("personalDetails", "currentCounty", v)
            }
          />

          <Field
            label="Postcode"
            value={form.personalDetails?.currentPostcode || ""}
            onChange={(v) =>
              updateSection("personalDetails", "currentPostcode", v)
            }
          />

          <Field
            label="Moving in date"
            type="month"
            value={toLocalMonthInputValue(form.personalDetails?.movingInDate)}
            onChange={(v) =>
              updateSection("personalDetails", "movingInDate", v)
            }
          />
        </div>
      </PostCard>
      {/* <PostCard title="Personal Details">
        <div className="smGrid2">
          <Field
            label="Date of birth"
            type="date"
            value={toLocalDateInputValue(form.personalDetails?.dob)}
            onChange={(v) => updateSection("personalDetails", "dob", v)}
          />

          <Field
            label="Nationality at birth"
            value={form.personalDetails?.nationalityAtBirth || ""}
            onChange={(v) =>
              updateSection("personalDetails", "nationalityAtBirth", v)
            }
          />

          <Field
            label="Town of birth"
            value={form.personalDetails?.townOfBirth || ""}
            onChange={(v) => updateSection("personalDetails", "townOfBirth", v)}
          />

          <Field
            label="Country of birth"
            value={form.personalDetails?.countryOfBirth || ""}
            onChange={(v) =>
              updateSection("personalDetails", "countryOfBirth", v)
            }
          />

          <Field
            label="Name change date"
            type="month"
            value={toLocalMonthInputValue(form.personalDetails?.nameChangeDate)}
            onChange={(v) =>
              updateSection("personalDetails", "nameChangeDate", v)
            }
          />

          <Field
            label="Lived in UK since"
            type="month"
            value={toLocalMonthInputValue(form.personalDetails?.durationInUk)}
            onChange={(v) =>
              updateSection("personalDetails", "durationInUk", v)
            }
          />
        </div>
      </PostCard> */}
      <PostCard title="Next of Kin">
        <div className="smGrid2">
          <Field
            label="Name"
            value={form.nextOfKinDetails?.name || ""}
            onChange={(v) => updateSection("nextOfKinDetails", "name", v)}
          />

          <Field
            label="Relationship"
            value={form.nextOfKinDetails?.relationship || ""}
            onChange={(v) =>
              updateSection("nextOfKinDetails", "relationship", v)
            }
          />

          <Field
            label="Email"
            type="email"
            value={form.nextOfKinDetails?.email || ""}
            onChange={(v) => updateSection("nextOfKinDetails", "email", v)}
          />

          <Field
            label="Telephone"
            value={
              form.nextOfKinDetails?.telephone
                ? formatUKPhone(form.nextOfKinDetails.telephone)
                : ""
            }
            onChange={(v) => updateSection("nextOfKinDetails", "telephone", v)}
          />

          <Field
            label="Address"
            full
            value={form.nextOfKinDetails?.address || ""}
            onChange={(v) => updateSection("nextOfKinDetails", "address", v)}
          />

          <Field
            label="Town"
            value={form.nextOfKinDetails?.town || ""}
            onChange={(v) => updateSection("nextOfKinDetails", "town", v)}
          />

          <Field
            label="County"
            value={form.nextOfKinDetails?.county || ""}
            onChange={(v) => updateSection("nextOfKinDetails", "county", v)}
          />

          <Field
            label="Postcode"
            value={form.nextOfKinDetails?.postcode || ""}
            onChange={(v) => updateSection("nextOfKinDetails", "postcode", v)}
          />
        </div>
      </PostCard>
      {/* <div className="smSectionTitle">AR/TS Details</div> */}
      {/* <div className="FP_Subtitle_Semibold">Business Details</div>
      <PostCard title="AR/TS Details">

        <div className="smGrid2">
          <Field
            label="Trading Name"
            value={form.adviserDoc?.tradingName || ""}
            onChange={(v) => updateAdviserDoc("tradingName", v)}
          />

          <Field
            label="FCA Registration Number (FRN)"
            value={form.personalDetails?.fcaNumber || ""}
            onChange={(v) => updateSection("personalDetails", "fcaNumber", v)}
          />

          <div className="smField smFieldFull">
            <div className="smTradingStyleRow">
              <label className="smFieldLabel">Trading Style</label>
              <AddIcon onClick={addTradingStyle} title="Add trading style" />
            </div>

            <div className="smTradingStylesWrap">
              {tradingStyles.map((style, index) => (
                <div
                  key={`trading-style-${index}`}
                  className="smTradingStyleRow"
                >
                  <input
                    className="smFieldInput"
                    type="text"
                    value={style || ""}
                    onChange={(e) =>
                      updateTradingStyleAtIndex(index, e.target.value)
                    }
                    placeholder={`Trading style ${index + 1}`}
                  />

                  <DeleteIcon
                    onClick={() => removeTradingStyle(index)}
                    title={`Delete trading style ${index + 1}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="smBusinessDivider" />

        <div className="smGrid2 smApplicationGrid">
          <RadioGroup
            label="Applying As"
            name="applyingAs"
            options={Object.values(ApplyingAs)}
            value={form.applicationDetails?.applyingAs || ""}
            onChange={(v) =>
              updateSection("applicationDetails", "applyingAs", v)
            }
            columns={1}
          />

          <RadioGroup
            label="Applying To Join"
            name="applyingToJoin"
            options={Object.values(ApplyingToJoin)}
            value={form.applicationDetails?.applyingToJoin || ""}
            onChange={(v) =>
              updateSection("applicationDetails", "applyingToJoin", v)
            }
            columns={1}
          />
          <CheckboxGroup
            label="Business Permissions"
            options={Object.values(BusinessPermissions)}
            values={form.applicationDetails?.businessAreas || []}
            onChange={(v) =>
              updateSection("applicationDetails", "businessAreas", v)
            }
            columns={2}
          />

          <CheckboxGroup
            label="AR Business Permissions"
            options={Object.values(BusinessPermissions)}
            values={form.applicationDetails?.ARBusinessAreas || []}
            onChange={(v) =>
              updateSection("applicationDetails", "ARBusinessAreas", v)
            }
            columns={2}
          />
        </div>
      </PostCard>
      <PostCard title="Business Address">
        <div className="smGrid2">
          <Field
            label="Address"
            full
            value={form.personalDetails?.businessAddress?.fullAddress || ""}
            onChange={(v) => updateBusinessAddress("fullAddress", v)}
          />
        </div>
      </PostCard>
      <PostCard title="Website">
        <div className="smGrid2">
          <Field
            label="Website"
            full
            value={form.adviserDoc?.website || ""}
            onChange={(v) => updateWebsite(v)}
            placeholder="https://example.com"
          />
        </div>
      </PostCard>
      <PostCard title="Regulatory Details">
        <div className="smGrid2">
          <Field
            label="Regulatory Text"
            type="textarea"
            full
            value={
              typeof form.adviserDoc?.regulatoryText === "string"
                ? form.adviserDoc.regulatoryText
                : form.adviserDoc?.regulatoryText?.text || ""
            }
            onChange={(v) => updateRegulatoryText(v)}
          />
        </div>
      </PostCard> */}
    </div>
  );
};

export default EditAdviserDetails;
